import { GridSortDirection } from '@mui/x-data-grid';
import { Workbench, WorkbenchConfiguration, WorkbenchConnected } from '~/api/types';
import { formatFilterQuery } from '~/utils';
import { v2Api } from '../v2Api';

const tags = ['workbench'] as const;

export type WorkbenchCreateQuery = {
  name: string;
  description: string;
  instance_type: string;
};

export const WORKBENCH_PAGE_SIZE = 50;

const workbenchApi = v2Api
  .enhanceEndpoints({
    addTagTypes: tags,
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getProjectWorkbenches: builder.query<
        { data: Workbench[] },
        {
          projectId: number;
          search?: string;
          statusList?: string[];
          showTerminated: boolean;
          createdOn?: string[];
          createdBy?: string[];
          typeList?: string[];
          sort?: string;
          order?: GridSortDirection;
        }
      >({
        query: ({ projectId, search, statusList, showTerminated, createdOn, createdBy, typeList, sort, order }) =>
          `projects/${projectId}/workbenches?size=${WORKBENCH_PAGE_SIZE}${formatFilterQuery(search, 'search')}${formatFilterQuery(createdOn, 'created')}${formatFilterQuery(String(showTerminated), 'show_terminated')}${formatFilterQuery(statusList, 'status_list')}${formatFilterQuery(createdBy, 'created_by_list')}${formatFilterQuery(typeList, 'type_list')}${formatFilterQuery(sort, 'sort')}${formatFilterQuery(order, 'order')}`,
        providesTags: tags,
      }),
      getConnectToWorkbench: builder.query<WorkbenchConnected, { workbenchId: number; projectId: number }>({
        query: ({ workbenchId, projectId }) => ({
          url: `projects/${projectId}/workbenches/${workbenchId}/connect`,
          method: 'GET',
        }),
        transformResponse: (response: { data: WorkbenchConnected }) => response.data,
      }),
      createWorkbench: builder.mutation<object, WorkbenchCreateQuery & { projectId: number }>({
        query: ({ projectId, ...body }) => ({
          url: `projects/${projectId}/workbenches`,
          method: 'POST',
          body,
        }),
        invalidatesTags: tags,
      }),
      startWorkbench: builder.mutation<object, { workbenchId: number; projectId: number }>({
        query: ({ workbenchId, projectId }) => ({
          url: `projects/${projectId}/workbenches/${workbenchId}/start`,
          method: 'PUT',
        }),
        invalidatesTags: tags,
      }),
      stopWorkbench: builder.mutation<object, { workbenchId: number; projectId: number }>({
        query: ({ workbenchId, projectId }) => ({
          url: `projects/${projectId}/workbenches/${workbenchId}/stop`,
          method: 'PUT',
        }),
        invalidatesTags: tags,
      }),
      terminateWorkbench: builder.mutation<object, { workbenchId: number; projectId: number }>({
        query: ({ workbenchId, projectId }) => ({
          url: `projects/${projectId}/workbenches/${workbenchId}/terminate`,
          method: 'PUT',
        }),
        invalidatesTags: tags,
      }),
      autoStopWorkbench: builder.mutation<object, { workbenchId: number; projectId: number }>({
        query: ({ workbenchId, projectId }) => ({
          url: `projects/${projectId}/workbenches/${workbenchId}/autostop`,
          method: 'PUT',
        }),
        invalidatesTags: tags,
      }),
      deleteAutoStopWorkbench: builder.mutation<object, { workbenchId: number; projectId: number }>({
        query: ({ workbenchId, projectId }) => ({
          url: `projects/${projectId}/workbenches/${workbenchId}/autostop`,
          method: 'DELETE',
        }),
        invalidatesTags: tags,
      }),
      getWorkbenchTypes: builder.query<WorkbenchConfiguration, { projectId: number }>({
        query: ({ projectId }) => `projects/${projectId}/workbenches/types`,
        transformResponse: (response: { data: WorkbenchConfiguration }) => response.data,
      }),
    }),
  });

export default workbenchApi;
